import React, { useState, useEffect } from 'react';
import { Search, Package, X, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const ProductSelector = ({ onProductSelect, currentLink = '' }) => {
  const { token } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  // Fetch products when modal opens
  useEffect(() => {
    if (isOpen) {
      setPage(1);
      fetchProducts({ page: 1, append: false });
    }
  }, [isOpen]);

  // Set selected product from current link
  useEffect(() => {
    if (currentLink && currentLink.startsWith('/products/')) {
      const productId = currentLink.split('/').pop();
      if (products.length > 0) {
        const product = products.find(p => p.id.toString() === productId);
        if (product) {
          setSelectedProduct(product);
        }
      }
    }
  }, [currentLink, products]);

  // Debounced search
  useEffect(() => {
    if (!isOpen) return;
    const id = setTimeout(() => {
      setPage(1);
      fetchProducts({ page: 1, append: false });
    }, 300);
    return () => clearTimeout(id);
  }, [searchTerm, isOpen]);

  const fetchProducts = async ({ page = 1, append = false } = {}) => {
    try {
      if (append) setLoadingMore(true); else setLoading(true);
      const params = new URLSearchParams({
        page: String(page),
        limit: '50',
        search: searchTerm || ''
      });
      const response = await fetch(`/api/admin/products?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (append) {
          setProducts(prev => [...prev, ...(data.products || [])]);
        } else {
          setProducts(data.products || []);
        }
        if (data.pagination) {
          setTotalPages(data.pagination.pages || 1);
        } else {
          setTotalPages(1);
        }
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    const link = `/products/${product.id}`;
    onProductSelect(link);
    setIsOpen(false);
  };

  const handleClearSelection = () => {
    setSelectedProduct(null);
    onProductSelect('');
  };

  const displayProducts = products;

  return (
    <>
      {/* Product Selection Button */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Lien du Bouton
        </label>
        
        {selectedProduct ? (
          <div className="space-y-3">
            {/* Selected Product Display */}
            <div className="bg-gray-600/50 rounded-lg p-4 border border-gray-500">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center">
                    <Package className="h-6 w-6 text-gray-900" />
                  </div>
                  <div>
                    <div className="text-white font-medium">{selectedProduct.name_fr}</div>
                    <div className="text-gray-300 text-sm">SKU: {selectedProduct.sku}</div>
                    <div className="text-yellow-400 text-sm">Lien: /products/{selectedProduct.id}</div>
                  </div>
                </div>
                <button
                  onClick={handleClearSelection}
                  className="text-red-400 hover:text-red-300 p-2 rounded-lg hover:bg-gray-600 transition-colors"
                  title="Supprimer la sélection"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            {/* Change Product Button */}
            <button
              type="button"
              onClick={() => setIsOpen(true)}
              className="w-full bg-gray-600 hover:bg-gray-500 text-white font-medium px-4 py-3 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <Package className="h-5 w-5" />
              <span>Changer de Produit</span>
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setIsOpen(true)}
            className="w-full bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-bold px-4 py-3 rounded-lg transition-colors flex items-center justify-center space-x-2"
          >
            <Package className="h-5 w-5" />
            <span>Sélectionner un Produit</span>
          </button>
        )}
        
        <p className="text-sm text-gray-400">
          Choisissez un produit pour générer automatiquement le lien du bouton
        </p>
      </div>

      {/* Product Selection Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-gray-700 to-gray-800 px-6 py-4 border-b border-gray-600">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  <div className="bg-yellow-500 p-2 rounded-lg">
                    <Package className="h-6 w-6 text-gray-900" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Sélectionner un Produit</h3>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Search Bar */}
            <div className="p-6 border-b border-gray-600">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Rechercher par nom, SKU..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-white placeholder-gray-400"
                />
              </div>
            </div>

            {/* Products List */}
            <div className="p-6 max-h-96 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
                </div>
              ) : displayProducts.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  {searchTerm ? 'Aucun produit trouvé' : 'Aucun produit disponible'}
                </div>
              ) : (
                <div className="grid gap-3">
                  {displayProducts.map((product) => (
                    <div
                      key={product.id}
                      onClick={() => handleProductSelect(product)}
                      className="bg-gray-700 hover:bg-gray-600 rounded-lg p-4 cursor-pointer transition-colors border border-gray-600 hover:border-gray-500"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-16 h-16 bg-gray-600 rounded-lg overflow-hidden">
                            {product.main_photo ? (
                              <img
                                src={product.main_photo}
                                alt={product.name_fr}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/64x64/374151/9CA3AF?text=No+Img';
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400">
                                <Package className="h-8 w-8" />
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="text-white font-medium text-xs">{product.name_fr}</div>
                            <div className="text-gray-300 text-sm">SKU: {product.sku}</div>
                            <div className="text-gray-400 text-sm">Catégorie: {product.category_name}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-yellow-400 font-semibold">
                            {new Intl.NumberFormat('fr-FR', {
                              style: 'currency',
                              currency: 'EUR'
                            }).format(product.price)}
                          </div>
                          <div className="text-gray-400 text-sm">
                            /products/{product.id}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Pagination / Load more */}
            {totalPages > page && (
              <div className="px-6 pb-6">
                <button
                  disabled={loadingMore}
                  onClick={() => {
                    const next = page + 1;
                    setPage(next);
                    fetchProducts({ page: next, append: true });
                  }}
                  className="w-full bg-gray-700 hover:bg-gray-600 text-white rounded-lg py-2 disabled:opacity-60"
                >
                  {loadingMore ? 'Chargement…' : 'Charger plus'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ProductSelector;
